import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AccountSecret } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';
import {
  revealAccountSecret,
  useAccountSecrets,
  useCreateAccountSecret,
  useDeleteAccountSecret,
  useUpdateAccountSecret,
} from '../../hooks/useAccountSecrets';
import { useCatalogSubscription } from '../../hooks/useCatalogSubscription';
import { SecretEditorDialog } from './SecretEditorDialog';
import { SecretRow } from './SecretRow';

export function SecretsView() {
  const { secretId } = useParams<{ secretId?: string }>();
  const navigate = useNavigate();
  useCatalogSubscription('secrets');

  const listQuery = useAccountSecrets();
  const createMutation = useCreateAccountSecret();
  const updateMutation = useUpdateAccountSecret();
  const deleteMutation = useDeleteAccountSecret();
  const secrets = listQuery.data?.secrets;

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AccountSecret | null>(null);
  const [deleting, setDeleting] = useState<AccountSecret | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);

  // A search hit deep-links to /secrets/{id} (secrets ADR-005); open that row.
  useEffect(() => {
    if (!secretId || !secrets) return;
    const match = secrets.find((s) => s.id === secretId);
    if (match) {
      setEditing(match);
      setEditorOpen(true);
    }
  }, [secretId, secrets]);

  const busy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const closeEditor = () => {
    setEditorOpen(false);
    setEditing(null);
    setEditorError(null);
    if (secretId) navigate('/secrets');
  };

  const submit = async (values: { name: string; value: string; description: string }) => {
    setEditorError(null);
    try {
      if (editing) {
        await updateMutation.mutateAsync({
          secretId: editing.id,
          req: {
            description: values.description,
            ...(values.value ? { value: values.value } : {}),
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: values.name,
          value: values.value,
          description: values.description,
        });
      }
      closeEditor();
    } catch (err) {
      setEditorError(err instanceof Error ? err.message : 'Could not save this secret');
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    await deleteMutation.mutateAsync(deleting.id);
    setDeleting(null);
  };

  return (
    <div className="p-4" data-testid="secrets-view">
      <div className="mb-4 flex items-start justify-end gap-4">
        <Button onClick={() => { setEditing(null); setEditorError(null); setEditorOpen(true); }} data-testid="new-secret-button">
          New secret
        </Button>
      </div>

      {listQuery.isError && (
        <p className="mb-3 text-body text-destructive" data-testid="secrets-list-error">
          Could not load secrets.
        </p>
      )}

      {secrets && secrets.length === 0 && (
        <p className="text-body text-muted-foreground" data-testid="secrets-empty">
          No secrets yet.
        </p>
      )}

      {secrets && secrets.length > 0 && (
        <ul data-testid="secrets-list">
          {secrets.map((secret) => (
            <SecretRow
              key={secret.id}
              secret={secret}
              disabled={busy}
              onEdit={(row) => { setEditing(row); setEditorError(null); setEditorOpen(true); }}
              onDelete={setDeleting}
              onReveal={(row) => revealAccountSecret(row.id)}
            />
          ))}
        </ul>
      )}

      <SecretEditorDialog
        open={editorOpen}
        editing={editing}
        busy={busy}
        error={editorError}
        onCancel={closeEditor}
        onSubmit={submit}
      />

      <Dialog open={deleting !== null} onOpenChange={(next) => !next && setDeleting(null)}>
        <DialogContent data-testid="secret-delete-confirm">
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>
              The value cannot be recovered, and any agent relying on this secret will stop
              working.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={busy} data-testid="secret-delete-confirm-button">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
